/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import { viewHref } from "@shared/navigation/tabs/ViewsProvider";
import GenericButton from '@ui-components/Button/GenericButton'
import GenericCard from '@ui-components/Card/GenericCard'
import { GenericTable, GenericTableColumn } from '@ui-components/Table/GenericTable'
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { getFilteredData, PortItem, portsDetailsColumns } from '../PortDetailsData';
import { usePhoneFold } from 'kontrolEdge/components/Shared/DetailSummary/usePhoneFold';
import { useWindowSize } from '@shared/hooks/useWindowSize';
import { fetchPortConnectionsAPI } from '../../requests/request';
import { useAxiosAuth0 } from '@shared/config/useAxiosAuth0';
import { useAppSelector } from '@redux/hooks';
import {
	CARRIER_BRIDGE_CONNECTION_TYPE,
	carrierBridgeConnectionPath,
	carrierBridgePortConnections,
} from 'kontrolEdge/components/CarrierBridge/carrierBridgePortConnections';

/** Where a connection row opens: its own detail, or "#" when it has none. */
function connectionRoute(row: PortItem): string {
	switch (row.type) {
		case "Enterprise Internet":
			return buildDetailPath("internet", { connectionId: row.id });
		case "Datacenter Interconnect":
			return buildDetailPath("datacenter", { datacenterId: row.id });
		case "Cloud Connectivity":
			return buildDetailPath("cloudConnectivity", { connectionId: row.id });
		case "FWint":
			return "/kontrol/fwaas";
		case CARRIER_BRIDGE_CONNECTION_TYPE:
			return row.id ? carrierBridgeConnectionPath(row.id) : "#";
		default:
			return "#";
	}
}

/** The connection's ID as the customer knows it; a firewall link names its FWaaS too. */
function connectionLabel(row: PortItem): string {
	if (row.type === "FWint") {
		const fwintId = row.internetEnterpriseSubsId || row.id || "-";
		return row.fwintFwaasSubsId ? `${fwintId} [FWaaS ${row.fwintFwaasSubsId}]` : fwintId;
	}
	return row.internetEnterpriseSubsId || row.subid || row.id || "-";
}

/** The kind a person reads: "FWint" is the firewall's link to the port. */
function connectionKind(row: PortItem): string {
	return row.type === "FWint" ? "Firewall" : row.type;
}

interface PortConnectionsProps {
	portId: string | null;
  }
interface PortConnections {
	id: string | null;
	name: string;
	subid: string;
	internetEnterprise: InternetEnterprise;
	hardware: Hardware;
}
interface InternetEnterprise {
	vlan: number;
	mbps: number;
	subscriptionId: string;
	routing: string;
	terms: string
}
interface Hardware {
	status: number;
}

function normalizeVlan(v: unknown): { vlanNumeric: number; vlanDisplay: string } {
	if (v == null) return { vlanNumeric: 0, vlanDisplay: "-" };
	if (typeof v === "number") return { vlanNumeric: v, vlanDisplay: String(v) };
  
	const s = String(v).trim();
	if (!s) return { vlanNumeric: 0, vlanDisplay: "-" };
  
	// Split on ';' or ',' and clean parts
	const parts = s.split(/[;,]/).map(p => p.trim()).filter(Boolean);
	const display = parts.join(", ");
	const first = Number(parts[0]);
	const vlanNumeric = Number.isFinite(first) ? first : 0;
  
	return { vlanNumeric, vlanDisplay: display || "-" };
  }
  

  export default function PortConnections({ portId }: PortConnectionsProps) {
	
	if (!portId) {
		return <></>;
	}

	function capitalizeWords(str: string): string {
	return str
		.replace(/([A-Z])/g, " $1")
		.trim()
		.replace(/\b\w/g, (char) => char.toUpperCase());
	}

	function shapeConnection(connection: any): PortItem {
		const parent = connection.parent ?? {};
		const t = String(connection.type ?? "").toLowerCase();
		const isFwint = t === "fwint";
		const { vlanNumeric, vlanDisplay } = normalizeVlan(connection.vlan);
	// Routing (IE only, but harmless to compute)
	const routingRaw = connection.routing ?? "";
	const routingFormatted = routingRaw ? capitalizeWords(routingRaw) : "-";
	
	// TERMS: support either `terms` OR `{ term, term_unit }`
	const termCount = connection.terms ?? connection.term;
	const rawUnit = connection.term_unit;
	// normalize unit label (month(s) / year(s)
	const unit = rawUnit ? String(rawUnit).toLowerCase() : "months";
	const normalizedUnit =
		unit.startsWith("month") ? (Number(termCount) === 1 ? "month" : "months")
		: unit.startsWith("year") ? (Number(termCount) === 1 ? "year" : "years")
		: unit; // fallback unchanged
	const termsFormatted = termCount ? `${termCount} ${normalizedUnit}` : "-";
	
	// Mbps: IE uses `mbps`, FWint uses `capacity`
	const mbps =
		isFwint
		? Number(connection.capacity ?? 0)
		: Number(connection.mbps ?? 0);
	
	// VLAN may be string; coerce to number
	const vlan = connection.vlan != null ? Number(connection.vlan) : 0;
	
	const item: PortItem = {
		id: connection.id ?? "-",
		portName: parent.name ?? "",
		subid: parent.subscriptionId ?? "",
		internetEnterpriseVlan: vlan,
		internetEnterpriseMbps: mbps,
		internetEnterpriseTerms: termsFormatted,
		internetEnterpriseSubsId: connection.subscriptionId ?? "",
		internetEnterpriseRouting: routingFormatted,
		cloud: connection.cloud ?? '-',
		provider:connection.provider ?? '-',
		account:connection.account ?? '-',
		autoRenew: connection.autoRenew ? "Active" : "Inactive",
		automaticVlan: connection.automaticVlan ? "Yes" : "No",
		type: connection.type ?? "-",
		hardwareStatus: parent.hardware?.status ?? 0,
		name: connection.name ?? parent.name ?? "",
		vlan: vlanNumeric,
		vlanDisplay,
	};
	
	// If this is FWint and it embeds a FWaaS, keep its subscriptionId for the label
	if (isFwint && connection.fwaas) {
		item.fwintFwaasSubsId =
		connection.fwaas.subscriptionId ?? connection.fwaas.name ?? undefined;
	}
	
	return item;
	}
	
	
	const navigate = useNavigate();
	const { tenantId } = useAppSelector(state => state.selection);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchColumn, setSearchColumn] = useState("All");
	const [tableData, setTableData] = useState<PortItem[]>([]);
	const [isFetching, setIsFetching] = useState(false);
	const [hasFetched, setHasFetched] = useState<boolean>(false);
	const filteredData = getFilteredData(tableData, searchTerm, searchColumn);
	//Port id selected from MainPort
	const api = useAxiosAuth0();

	useEffect(() => {
		const fetchData = async () => {
		try {
		setIsFetching(true);
		const response = await fetchPortConnectionsAPI(api, portId, tenantId);
		const data = response.data.data;

		const arrify = <T,>(v: T | T[] | null | undefined): T[] =>
			Array.isArray(v) ? v : v ? [v] : [];

		const flattenedConnections: any[] = [];

		// Internet Enterprise
		arrify(data.internetEnterprise).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "Enterprise Internet",
			parent: data,
		});
		});

		// Datacenter Interconnect (API may return null or object)
		arrify(data.datacenterInterconnect).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "Datacenter Interconnect",
			parent: data,
		});
		});

		// Cloud Connectivity
		arrify(data.cloudConnectivity).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "Cloud Connectivity",
			parent: data,
		});
		});

		// FWINT
		arrify(data.fwint).slice(0, 1).forEach((conn: any) => {
			flattenedConnections.push({ ...conn, type: "FWint", parent: data });
		});

		// SD-WAN Interconnect (hub ↔ port link)
		arrify(data.sdwanInt).forEach((conn: any) => {
		flattenedConnections.push({
			...conn,
			type: "SD-WAN Interconnect",
			parent: data,
		});
		});

		// Carrier Bridge (CTX-947)
		flattenedConnections.push(...carrierBridgePortConnections(data));
	
		const shapedConnections = flattenedConnections.map((conn: any) =>
			shapeConnection(conn)
		);
	
		setTableData(shapedConnections);
		} catch (error) {
		console.error("Error fetching port connections", error);
		} finally {
		setIsFetching(false);
		setHasFetched(true);
		}
	};
	
	fetchData();
	}, [api, portId]);
	

	const handleCreateConnection = () => {
		navigate("/kontrol/add-ipx")
	};
	const columns = portsDetailsColumns();
	const isPhone = useWindowSize().width < 640;

	// Folded on a phone like the detail's other cards: how many connections ride
	// the port, and of which kinds.
	const kinds = Array.from(new Set(filteredData.map(connectionKind)));
	const fold = usePhoneFold(
		{ header: "Associated Connections", items: [] },
		{ isCollapsible: false },
		filteredData.length === 0
			? "No connections"
			: `${filteredData.length} ${filteredData.length === 1 ? "connection" : "connections"} · ${kinds.join(", ")}`
	);

	const updatedColumns = columns.map((col: GenericTableColumn<PortItem>) => {
	if (col.header === "Connection Id") {
		return {
		...col,
		renderCell: (row: PortItem) => {
			const route = connectionRoute(row);
			return (
			<button
				{...viewHref(route.startsWith("/") ? route : null)}
				onClick={() => navigate(route)}
				className="hover:text-semantic-text-brand hover:underline focus:underline active:underline font-medium underline text-semantic-text-brand"
			>
				{connectionLabel(row)}
			</button>
			);
		},
		};
	}
	return col;
  });

  return (
    <>
		<div className="flex w-full justify-end mb-5">
			{/* <GenericButton
				label={
					<div className="flex items-center gap-2">
						<IconPlus className="w-4 h-4" />
						<span>Create Connection</span>
					</div>
				}
				variant="secondary"
				shape="ghost"
				size="md"
				disabled={true}
				onClick={handleCreateConnection}
			/> */}
		</div>
		<GenericCard
			isLoading={isFetching}
			{...fold}
			bodyContent={
				isPhone ? (
					// On a phone: one row per connection that opens its detail, which
					// holds its term, routing and VLAN settings (CTX-1421).
					filteredData.length === 0 ? (
						<p className="text-sm text-semantic-text-secondary">No connections on this port.</p>
					) : (
						<ul className="divide-y divide-semantic-border-secondary">
							{filteredData.map((row, index) => {
								const route = connectionRoute(row);
								const facts = [
									connectionKind(row),
									row.internetEnterpriseMbps ? `${row.internetEnterpriseMbps} Mbps` : null,
									row.vlanDisplay && row.vlanDisplay !== "-" ? `VLAN ${row.vlanDisplay}` : null,
								].filter(Boolean);
								return (
									<li key={row.id ?? index}>
										<button
											type="button"
											{...viewHref(route.startsWith("/") ? route : null)}
											onClick={() => navigate(route)}
											disabled={route === "#"}
											className="flex min-h-11 w-full items-center gap-3 py-2.5 text-left"
										>
											<span className="min-w-0 flex-1">
												<span className="block truncate text-sm font-semibold text-semantic-text-brand">
													{connectionLabel(row)}
												</span>
												<span className="block truncate text-xs text-semantic-text-secondary">
													{facts.join(" · ")}
												</span>
											</span>
											{route !== "#" && (
												<span aria-hidden className="text-lg text-semantic-text-secondary">›</span>
											)}
										</button>
									</li>
								);
							})}
						</ul>
					)
				) : (
					<GenericTable
					columns={updatedColumns}
					data={filteredData}
					initialRowsPerPage={5}
					rowsPerPageOptions={[5, 10, 20]}
					width="100%"
					tableClassName="bg-semantic-bg-primary"
				/>
				)
			}
		>
		</GenericCard>
	</>
  )
}
